import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import axios from "axios";
import InfiniteScroll from "react-infinite-scroll-component";

import SkeletonLoader from "../SkeletonLoader";
import ProductCard from "../ProductCard";
import { useAuth } from "../../contexts/AuthContext";
import Spinner from "../Spinner";

const NO_DATA_DELAY_MS = 5000;

const CategoryPage = () => {
  const { id } = useParams();
  const [productsArray, setProductsArray] = useState([]);
  const [totalData, setTotalData] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [phase, setPhase] = useState("spinner");
  const [page, setPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const { setCategory } = useAuth();
  const cacheKey = `category_products_${id}`;
  const phaseTimerRef = useRef(null);
  const navigate = useNavigate();

  const [noDataReady, setNoDataReady] = useState(false);
  const noDataTimerRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/category/${id}`)
      .then((response) => {
        if (response?.data?.data && response?.data?.statusCode === 1) {
          setCategory(response.data.data);
        } else {
          setCategory({});
        }
      })
      .catch(() => setCategory({}));
  }, [id, setCategory]);

  // Reset when category changes
  useEffect(() => {
    setProductsArray([]);
    setTotalData(20);
    setHasMore(true);
    setPage(1);
    setPhase("spinner");

    // reset “No Data” reveal gate
    setNoDataReady(false);
    if (noDataTimerRef.current) {
      clearTimeout(noDataTimerRef.current);
      noDataTimerRef.current = null;
    }
  }, [id]);

  useEffect(() => {
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);

    if (isFetching) {
      setPhase("spinner");
      phaseTimerRef.current = setTimeout(() => {
        if (isFetching) setPhase("skeleton");
      }, 600);
    } else {
      setPhase("done");
    }

    return () => {
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    };
  }, [isFetching, id, page]);

  const handleProductData = async () => {
    if (productsArray.length >= totalData && page > 1) {
      setHasMore(false);
      return;
    }

    let url = `${process.env.REACT_APP_API_URL}/api/products/get?limit=20&page=${page}`;
    if (id) url += `&category=${id}`;

    try {
      setIsFetching(true);
      const res = await axios.get(url);

      const list = res?.data?.data || [];
      const ok = res?.data?.statusCode === 1;

      if (ok && list.length > 0) {
        setProductsArray((prev) => {
          const updated = [...prev, ...list];

          // cache only when we have data
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: updated,
              total: res?.data?.total ?? 20,
              timestamp: Date.now(),
            })
          );

          return updated;
        });
        if (res?.data?.total) setTotalData(res.data.total);

        setHasMore(
          productsArray.length + list.length < (res?.data?.total ?? 0)
        );

        if (noDataTimerRef.current) {
          clearTimeout(noDataTimerRef.current);
          noDataTimerRef.current = null;
        }
        setNoDataReady(false);
      } else {
        if (res?.data?.total) setTotalData(res.data.total);
        setHasMore(false);

        if (page === 1 && !noDataTimerRef.current) {
          noDataTimerRef.current = setTimeout(() => {
            setNoDataReady(true);
            noDataTimerRef.current = null;
          }, NO_DATA_DELAY_MS);
        }
      }
    } catch (err) {
      console.log("---- error", err);
      if (page === 1) setProductsArray([]);
      setHasMore(false);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (page === 1) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const age = Date.now() - (parsed.timestamp || 0);

        // cache use only if it has data
        if (age < 5 * 60 * 1000 && (parsed.data?.length || 0) > 0) {
          setProductsArray(parsed.data || []);
          setTotalData(parsed.total || 20);
          setHasMore((parsed.data?.length || 0) < (parsed.total || 0));
          setPhase("done");

          // cached data ⇒ No “no data” timer
          if (noDataTimerRef.current) {
            clearTimeout(noDataTimerRef.current);
            noDataTimerRef.current = null;
          }
          setNoDataReady(false);
          return;
        }
      }
    }
    handleProductData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, id]);

  const renderSkeletonRow = () => (
    <Row xs={2} md={3} lg={6} className="g-1 g-lg-3 mt-2">
      {Array.from({ length: 6 }).map((_, idx) => (
        <Col key={`sk-${idx}`}>
          <SkeletonLoader />
        </Col>
      ))}
    </Row>
  );

  const renderCardsRow = () => (
    <Row xs={2} md={3} lg={6} className="g-1 g-lg-3 mt-2">
      {productsArray.map((item, index) => (
        <ProductCard key={item._id || index} item={item} index={index} />
      ))}
    </Row>
  );

  const showNoData = noDataReady && !isFetching && productsArray.length === 0;

  return (
    <InfiniteScroll
      dataLength={productsArray.length}
      next={() => {
        if (hasMore && !isFetching) setPage((prev) => prev + 1);
      }}
      hasMore={hasMore}
      loader={null}
    >
      {(phase === "spinner" ||
        (productsArray.length === 0 && !noDataReady)) && (
        <Col xs={12} className="p-0 mt-3">
          <Spinner />
        </Col>
      )}

      {phase === "skeleton" && productsArray.length > 0 && renderSkeletonRow()}

      {phase === "done" && productsArray.length > 0 && renderCardsRow()}

      {showNoData && (
        <div
          className="my-5 d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "40vh" }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#727272",
              textAlign: "center",
              marginBottom: "12px",
            }}
          >
            No Data Found!!
          </h3>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{
                border: "none",
                outline: "none",
                padding: "10px 16px",
                borderRadius: "8px",
                background: process.env.REACT_APP_NAV_THEME_COLOR,
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Shop Now
            </button>
          </div>
        </div>
      )}
    </InfiniteScroll>
  );
};

export default CategoryPage;
